import React, { useState, KeyboardEvent, useEffect } from "react";
import { styled, Theme, useTheme } from "@mui/material/styles";
import { TextField, Box, InputAdornment } from "@mui/material";
import { useSelector } from "react-redux";
import {
  selectTotal,
  selectCategoryProductsTotal,
  selectIsPreviewMode,
  selectProductListsFetched,
  selectIsFetchingCategoryProductIds,
} from "../store/product-list/ProductListSelectors";
import ButtonComponent from "./common/ButtonComponent";
import ProductPaginationBar from "./ProductPaginationBar";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import { selectProducts } from "store/product/ProductSelectors";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ContentSlot } from "store/product-list/ProductListTypes";
import { getAdjustedIndexWithContentSlots } from "../utils/ProductUtil";

const Root = styled(Box)({
  display: "flex",
  paddingTop: "10px",
});

const SearchContainer = styled(Box)({
  display: "flex",
  height: "25px",
});

const SearchCountText = styled("span")({
  fontSize: "0.875rem",
  color: "#666",
});

const searchInputStyles = {
  width: "197px",
  "& .MuiInputBase-root": {
    height: "26px !important",
    paddingRight: "4px !important",
  },
  "& .MuiFormLabel-root": {
    fontSize: "14px",
    fontWeight: 400,
    fontFamily: "Helvetica",
    top: "-8px",
  },
  "& .MuiFormLabel-root.Mui-focused": {
    fontSize: "14px",
    fontWeight: 400,
    fontFamily: "Helvetica",
    top: "0px",
  },
  "& .MuiInputBase-root.Mui-focused fieldset": {
    borderColor: (theme: Theme) => theme.palette?.focusColor?.main,
  },
};

interface Props {
  page: number;
  onPageChange: (
    event: React.ChangeEvent<unknown>,
    page: number,
    isFromPagination: boolean,
  ) => void;
  pageSize?: number;
  onSearchChange: (value: string, productId?: string) => void;
  productListRef?: React.RefObject<{
    scrollToProductIndex: (productIndex: number) => void;
  }>;
  contentSlots?: ContentSlot[];
}

const ProductListPagination: React.FC<Props> = ({
  page,
  onPageChange,
  pageSize = 24,
  onSearchChange,
  productListRef,
  contentSlots = [],
}) => {
  const theme = useTheme();
  const total = useSelector(selectTotal);
  // Full API total used for pagination controls, but may be filtered down in preview mode
  const apiTotal = useSelector(selectCategoryProductsTotal);
  const allProducts = useSelector(selectProducts);
  const productSequences = useSelector(selectProductListsFetched);
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const isFetchingCategoryProductIds = useSelector(selectIsFetchingCategoryProductIds);

  // When in preview mode, use filtered product count
  // Otherwise use the full API total (so all pages are visible from the start)
  const effectiveTotal = isPreviewMode ? productSequences.length : (apiTotal ?? total ?? 0);

  const pageCount = Math.ceil(
    (effectiveTotal + (contentSlots?.length || 0)) / pageSize,
  );

  // While V2 category product calls are in progress, only enable pages whose IDs have loaded
  const loadedPageCount = isFetchingCategoryProductIds
    ? Math.ceil((productSequences.length + (contentSlots?.length || 0)) / pageSize)
    : undefined;
  const [searchText, setSearchText] = useState("");
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const categoryId = useSelector(selectCurrentCategory);
  const shouldDisableUpButton =
    filteredProducts.length === 0 || currentSearchIndex === 0;
  const shouldDisableDownButton =
    filteredProducts.length === 0 ||
    currentSearchIndex + 1 === filteredProducts.length;
  const navigationButtonStyles = {
    minWidth: "23px",
    minHeight: "26px",
    margin: 0,
  };
  const navigationUpButtonStyles = {
    ...navigationButtonStyles,
    opacity: shouldDisableUpButton ? 0.5 : 1,
    filter: shouldDisableUpButton ? "grayscale(100%)" : "none",
  };

  const navigationDownButtonStyles = {
    ...navigationButtonStyles,
    opacity: shouldDisableDownButton ? 0.5 : 1,
    filter: shouldDisableDownButton ? "grayscale(100%)" : "none",
  };

  const arrowButtonStyles = {
    color: theme.palette?.neutral?.white,
  };

  // Reset to page 1 when category changes
  useEffect(() => {
    const resetToFirstPage = () => {
      if (page !== 1) {
        onPageChange({} as React.ChangeEvent<unknown>, 1, false);
      }
      // Reset search state when category changes
      setSearchText("");
      setFilteredProducts([]);
      setCurrentSearchIndex(0);
      onSearchChange("", ""); // Reset search in parent component
    };
    resetToFirstPage();
  }, [categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-filter products when preview mode changes
  useEffect(() => {
    if (searchText) {
      filterProducts(searchText);
    }
  }, [isPreviewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter products based on search query
  const filterProducts = (searchKey: string) => {
    if (!searchKey) {
      setFilteredProducts([]);
      setCurrentSearchIndex(0);
      onSearchChange("", "");
      return;
    }

    const searchLower = searchKey.toLowerCase();
    // Use productSequences to maintain order and respect preview mode filters
    const filtered = productSequences
      .map((seq) => allProducts[seq.productId])
      .filter(
        (product) =>
          product?.code?.toLowerCase().includes(searchLower) ||
          product?.name?.toLowerCase().includes(searchLower),
      );

    setFilteredProducts(filtered);
    setCurrentSearchIndex(0);

    if (filtered.length > 0) {
      const firstProduct = filtered[0];
      navigateToProduct(firstProduct);
      onSearchChange(searchKey, firstProduct.productId);
    } else {
      onSearchChange(searchKey, "");
    }
  };

  const navigateToProduct = (product: any) => {
    const productIndex = productSequences.findIndex(
      (seq) => seq.productId === product.productId,
    );
    if (productIndex >= 0) {
      const adjustedGlobalIndex = getAdjustedIndexWithContentSlots(
        productIndex,
        contentSlots,
      );
      const newPage = Math.ceil((adjustedGlobalIndex + 1) / pageSize);

      // Only scroll if we're already on the correct page
      if (newPage === page) {
        productListRef?.current?.scrollToProductIndex(adjustedGlobalIndex);
      } else {
        // If we need to change page, do that first and let the page change trigger the scroll
        handlePageChange({} as React.ChangeEvent<unknown>, newPage, false);
        // Wait for page change to complete
        setTimeout(() => {
          productListRef?.current?.scrollToProductIndex(adjustedGlobalIndex);
        }, 100);
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    // Clear previous search results
    if (!value) {
      setFilteredProducts([]);
      setCurrentSearchIndex(0);
      onSearchChange("", "");
      return;
    }

    // Immediately filter and navigate for new search
    filterProducts(value);
  };

  const handleNextResult = () => {
    if (!searchText || filteredProducts.length === 0) return;

    let newIndex = currentSearchIndex + 1;
    if (newIndex >= filteredProducts.length) {
      newIndex = 0;
    }

    setCurrentSearchIndex(newIndex);
    const nextProduct = filteredProducts[newIndex];
    navigateToProduct(nextProduct);
    onSearchChange(searchText, nextProduct.productId); // Pass the current product ID
  };

  const handlePrevResult = () => {
    if (!searchText || filteredProducts.length === 0) return;

    let newIndex = currentSearchIndex - 1;
    if (newIndex < 0) {
      newIndex = filteredProducts.length - 1;
    }

    setCurrentSearchIndex(newIndex);
    const prevProduct = filteredProducts[newIndex];
    navigateToProduct(prevProduct);
    onSearchChange(searchText, prevProduct.productId); // Pass the current product ID
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) {
        handlePrevResult();
      } else {
        handleNextResult();
      }
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
    isFromPagination: boolean = true,
  ) => {
    onPageChange(event, newPage, isFromPagination);
  };

  return (
    <Root>
      <SearchContainer>
        <TextField
          value={searchText}
          onChange={handleSearchChange}
          placeholder="Search products"
          onKeyDown={handleSearchKeyDown}
          id="product-search"
          name="product-search"
          type="text"
          variant="outlined"
          size="small"
          sx={searchInputStyles}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchCountText>
                  {filteredProducts.length > 0
                    ? `${currentSearchIndex + 1}/${filteredProducts.length}`
                    : "0/0"}
                </SearchCountText>
              </InputAdornment>
            ),
          }}
        />
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={handlePrevResult}
          disabled={shouldDisableUpButton}
          style={navigationUpButtonStyles}
          padding="0px"
        >
          <KeyboardArrowUpIcon sx={arrowButtonStyles} />
        </ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={handleNextResult}
          disabled={shouldDisableDownButton}
          style={navigationDownButtonStyles}
          padding="0px"
        >
          <KeyboardArrowDownIcon sx={arrowButtonStyles} />
        </ButtonComponent>
      </SearchContainer>

      <ProductPaginationBar
        page={page}
        pageCount={pageCount}
        loadedPageCount={loadedPageCount}
        onPageChange={handlePageChange}
      />
    </Root>
  );
};

export default ProductListPagination;
